import React, { useEffect, useState } from "react";
import { adminApi } from "../../apiAdmin";
import { byCurrencyPriority, formatTxnAmount } from "../../utils/currency";
import type { AdminFanDetail, AdminFanOrder, AdminRefundPreview } from "../../types/admin";
import FanFace from "./FanFace";
import {
  PROVIDER_LABEL,
  appLabels,
  contactLine,
  keptMoney,
  orderAmountText,
  orderStatusText,
  orderTitle,
  refundBlockedText,
  refundOffered,
  shortDate,
  whenText,
} from "./format";

// One fan, opened from a row of the Fans tab: who they are, what they spent
// (each currency on its own), their counts, their latest requests (each
// played one can be refunded), and the photo pull. A right-hand panel on
// desktop, a full-width sheet on a phone (AdminFansTab.css). Escape, the
// backdrop and × close it.

interface Props {
  fanId: string;
  onClose: () => void;
  onPhotoRemoved: (fanId: string) => void;
  onRefunded: () => void;
}

// A refund of one order, step by step. The server's preview decides: the
// exact figures to confirm, or why not (the talent's own refund rules —
// founder: "whatever doesn't make us lose money").
type Refund =
  | { orderId: string; phase: "checking" }
  | { orderId: string; phase: "blocked"; text: string }
  | { orderId: string; phase: "confirm" | "refunding"; preview: Extract<AdminRefundPreview, { refundable: true }> };

export default function FanDrawer({ fanId, onClose, onPhotoRemoved, onRefunded }: Props) {
  const [detail, setDetail] = useState<AdminFanDetail | null>(null);
  const [failed, setFailed] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [refund, setRefund] = useState<Refund | null>(null);

  useEffect(() => {
    let live = true;
    setDetail(null);
    setFailed(false);
    adminApi
      .get<AdminFanDetail>(`/fans/${fanId}`)
      .then((r) => live && setDetail(r.data))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [fanId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const removePhoto = async () => {
    if (!detail) return;
    if (!window.confirm(`Remove ${detail.fan.name || "this fan"}'s profile photo? That photo can't be used again.`)) return;
    setRemoving(true);
    try {
      await adminApi.post(`/fans/${fanId}/pull-photo`);
      setDetail((d) => d && { ...d, fan: { ...d.fan, avatarUrl: null } });
      onPhotoRemoved(fanId);
    } catch (err: any) {
      alert(err.response?.data?.msg || "Couldn't remove the photo. Try again.");
    } finally {
      setRemoving(false);
    }
  };

  const startRefund = async (o: AdminFanOrder) => {
    setRefund({ orderId: o._id, phase: "checking" });
    try {
      const { data } = await adminApi.get<AdminRefundPreview>(`/orders/${o._id}/refund-preview`);
      setRefund(
        data.refundable
          ? { orderId: o._id, phase: "confirm", preview: data }
          : { orderId: o._id, phase: "blocked", text: refundBlockedText(data.reason) }
      );
    } catch (err: any) {
      setRefund({ orderId: o._id, phase: "blocked", text: err.response?.data?.msg || "Couldn't check this refund. Try again." });
    }
  };

  const confirmRefund = async () => {
    if (!refund || refund.phase !== "confirm") return;
    const { orderId, preview } = refund;
    setRefund({ orderId, phase: "refunding", preview });
    try {
      await adminApi.post(`/orders/${orderId}/refund`);
      setRefund(null);
      const { data } = await adminApi.get<AdminFanDetail>(`/fans/${fanId}`);
      setDetail(data);
      onRefunded();
    } catch (err: any) {
      const body = err.response?.data;
      const text = body?.reason
        ? refundBlockedText(body.reason)
        : "The refund didn't go through. Check Stripe before trying again.";
      setRefund({ orderId, phase: "blocked", text });
    }
  };

  return (
    <div className="adm-fans-backdrop" onClick={onClose}>
      <aside className="adm-fans-drawer" role="dialog" aria-modal="true" aria-label="Fan" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="adm-fans-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        {failed ? (
          <p className="muted-text">Couldn't load this fan.</p>
        ) : !detail ? (
          <p className="muted-text">Loading…</p>
        ) : (
          <FanDetail
            detail={detail}
            removing={removing}
            onRemovePhoto={removePhoto}
            refund={refund}
            onRefund={startRefund}
            onConfirmRefund={confirmRefund}
            onCancelRefund={() => setRefund(null)}
          />
        )}
      </aside>
    </div>
  );
}

interface DetailProps {
  detail: AdminFanDetail;
  removing: boolean;
  onRemovePhoto: () => void;
  refund: Refund | null;
  onRefund: (o: AdminFanOrder) => void;
  onConfirmRefund: () => void;
  onCancelRefund: () => void;
}

function FanDetail({ detail, removing, onRemovePhoto, refund, onRefund, onConfirmRefund, onCancelRefund }: DetailProps) {
  const { fan, orders } = detail;
  const apps = appLabels(fan.appPlatforms);
  const spent = Object.keys(fan.spentByCurrency).sort(byCurrencyPriority);
  const tiles = [
    ...(spent.length
      ? spent.map((c) => ({ value: formatTxnAmount(fan.spentByCurrency[c], c), label: `Spent (${c.toUpperCase()})` }))
      : [{ value: "—", label: "Spent" }]),
    { value: String(fan.requests), label: "Requests" },
    { value: String(fan.played), label: "Played" },
    { value: String(fan.refunded), label: "Refunded" },
    { value: String(fan.tips), label: "Tips" },
    ...(fan.appLastSeenAt ? [{ value: whenText(fan.appLastSeenAt), label: "Last on the app" }] : []),
  ];
  return (
    <>
      <div className="adm-fans-drawer-head">
        <FanFace fan={fan} size="lg" />
        <div className="adm-fans-who">
          <h4 className={fan.name ? "" : "muted-text"}>{fan.name || "No name yet"}</h4>
          <span className="muted-text adm-fs-76">{contactLine(fan)}</span>
        </div>
      </div>
      <p className="muted-text adm-fs-76 adm-fans-drawer-meta">
        Signed in with {PROVIDER_LABEL[fan.provider]} · Joined {shortDate(fan.createdAt)} · {apps.length ? `${apps.join(" + ")} app` : "Web only"}
      </p>
      <div className="adm-fans-kv">
        {tiles.map((t) => (
          <div key={t.label}>
            <b>{t.value}</b>
            <span>{t.label}</span>
          </div>
        ))}
      </div>
      {orders.length === 0 ? (
        <p className="muted-text adm-fs-80">No requests yet.</p>
      ) : (
        <ul className="adm-fans-orders">
          {orders.map((o) => (
            <li key={o._id}>
              <div className="adm-fans-order-line">
                <span className="adm-fans-order-main">
                  <span className="adm-fans-order-title" title={[orderTitle(o), ...o.trackArtists].join(" · ")}>
                    {orderTitle(o)}
                  </span>
                  <em>
                    {o.dj?.name || "Deleted talent"} · {orderStatusText(o)} · {shortDate(o.createdAt)}
                  </em>
                </span>
                <span className="adm-fans-order-side">
                  <span className={keptMoney(o) ? "adm-fans-order-amount" : "adm-fans-order-amount adm-grey"}>{orderAmountText(o)}</span>
                  {refundOffered(o) && refund?.orderId !== o._id && (
                    <button type="button" className="adm-fans-refund" disabled={!!refund && refund.phase === "refunding"} onClick={() => onRefund(o)}>
                      Refund
                    </button>
                  )}
                </span>
              </div>
              {refund?.orderId === o._id && (
                <RefundPanel refund={refund} fanName={fan.name} djName={o.dj?.name || null} onConfirm={onConfirmRefund} onCancel={onCancelRefund} />
              )}
            </li>
          ))}
        </ul>
      )}
      {orders.length >= 100 && <p className="muted-text adm-fs-72">The latest 100 are shown.</p>}
      {fan.avatarUrl && (
        <button type="button" className="adm-btn-light adm-fans-pull" disabled={removing} onClick={onRemovePhoto}>
          {removing ? "Removing…" : "Remove profile photo"}
        </button>
      )}
    </>
  );
}

// The confirm sheet under the order: the exact figures from the server, then
// one tap. Or the reason it can't be refunded.
function RefundPanel({ refund, fanName, djName, onConfirm, onCancel }: { refund: Refund; fanName: string | null; djName: string | null; onConfirm: () => void; onCancel: () => void }) {
  if (refund.phase === "checking") return <div className="adm-fans-refund-panel muted-text">Checking…</div>;
  if (refund.phase === "blocked") {
    return (
      <div className="adm-fans-refund-panel" role="alert">
        <p>{refund.text}</p>
        <button type="button" className="adm-btn-light" onClick={onCancel}>
          OK
        </button>
      </div>
    );
  }
  const { preview } = refund;
  const money = (n: number) => formatTxnAmount(n, preview.currency);
  return (
    <div className="adm-fans-refund-panel">
      <p className="adm-semibold">
        Refund {money(preview.fanTotal)} to {fanName || "this fan"}?
      </p>
      <p className="muted-text adm-fs-76">
        {djName || "The talent"}'s earnings go down by {money(preview.djDrop)}
        {preview.cardFee ? ` and they cover the ${money(preview.cardFee)} card fee` : ""}. Request App pays nothing.
      </p>
      <div className="adm-fans-refund-actions">
        <button type="button" className="adm-btn-red" disabled={refund.phase === "refunding"} onClick={onConfirm}>
          {refund.phase === "refunding" ? "Refunding…" : `Refund ${money(preview.fanTotal)}`}
        </button>
        <button type="button" className="adm-btn-light" disabled={refund.phase === "refunding"} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
